import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { ConfigProvider, Tour } from "antd";
import type { TourStepProps } from "antd";
import { Button } from "../Button";
import type { GuideTourLabels, GuideTourProps } from "./types";
import { GUIDE_TOUR_DEFAULT_PANEL_WIDTH } from "./types";
import styles from "./GuideTour.module.css";

function formatGuideTourPanelWidth(width: number | string): string {
  return typeof width === "number" ? `${width}px` : width;
}

const DEFAULT_LABELS: Required<GuideTourLabels> = {
  skip: "跳过引导",
  prev: "上一步",
  next: "下一步",
  finish: "知道了",
};

/** 左右默认顶对齐；上下未指定横向时居中，显式 Left/Right 则保留 */
function resolveGuideTourPlacement(
  placement: TourStepProps["placement"] | undefined,
  isCenter: boolean
): TourStepProps["placement"] {
  if (isCenter) return "center";
  if (!placement) return "leftTop";

  switch (placement) {
    case "left":
    case "leftBottom":
      return "leftTop";
    case "leftTop":
      return "leftTop";
    case "right":
    case "rightBottom":
      return "rightTop";
    case "rightTop":
      return "rightTop";
    case "top":
      return "top";
    case "topLeft":
      return "topLeft";
    case "topRight":
      return "topRight";
    case "bottom":
      return "bottom";
    case "bottomLeft":
      return "bottomLeft";
    case "bottomRight":
      return "bottomRight";
    default:
      return placement;
  }
}

type GuideTourHeaderProps = {
  current: number;
  total: number;
};

function GuideTourHeader({ current, total }: GuideTourHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.progressBars} aria-hidden>
        {Array.from({ length: total }).map((_, index) => (
          <span
            key={index}
            className={`${styles.bar}${index === current ? ` ${styles.barActive}` : ""}`}
          />
        ))}
      </div>
      <span className={styles.stepCounter}>
        {current + 1}/{total}
      </span>
    </div>
  );
}

type GuideTourActionsProps = {
  current: number;
  total: number;
  labels: Required<GuideTourLabels>;
  onSkip: () => void;
  onPrev: () => void;
  onNext: () => void;
};

function GuideTourActions({
  current,
  total,
  labels,
  onSkip,
  onPrev,
  onNext,
}: GuideTourActionsProps) {
  const isLast = current >= total - 1;

  return (
    <div className={styles.footer}>
      <button type="button" className={styles.skipBtn} onClick={onSkip}>
        {labels.skip}
      </button>
      <div className={styles.actionBtns}>
        {current > 0 ? (
          <Button variant="default" size="sm" onClick={onPrev}>
            {labels.prev}
          </Button>
        ) : null}
        <Button variant="primary" size="sm" onClick={onNext}>
          {isLast ? labels.finish : labels.next}
        </Button>
      </div>
    </div>
  );
}

export function GuideTour({
  open,
  steps,
  current: controlledCurrent,
  defaultCurrent = 0,
  onChange,
  onFinish,
  onSkip,
  onClose,
  labels: labelsProp,
  mask = { color: "rgba(0, 0, 0, 0.6)" },
  gap,
  panelWidth = GUIDE_TOUR_DEFAULT_PANEL_WIDTH,
  rootClassName,
  className,
  locale,
  getPopupContainer,
}: GuideTourProps) {
  const [innerCurrent, setInnerCurrent] = useState(defaultCurrent);
  const current = controlledCurrent ?? innerCurrent;
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const total = steps.length;

  const setCurrent = (next: number) => {
    if (controlledCurrent === undefined) {
      setInnerCurrent(next);
    }
    onChange?.(next);
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleSkip = () => {
    onSkip?.();
    handleClose();
  };

  const handlePrev = () => {
    if (current > 0) {
      setCurrent(current - 1);
    }
  };

  const handleNext = () => {
    if (current >= total - 1) {
      onFinish?.();
      handleClose();
      return;
    }
    setCurrent(current + 1);
  };

  useEffect(() => {
    if (open) return;
    if (controlledCurrent === undefined) {
      setInnerCurrent(defaultCurrent);
    }
  }, [open, controlledCurrent, defaultCurrent]);

  const tourSteps = useMemo<TourStepProps[]>(
    () =>
      steps.map((step) => {
        const isCenter = step.target == null;
        const body = step.content ?? step.description;

        return {
          target: (isCenter ? null : step.target) as TourStepProps["target"],
          placement: resolveGuideTourPlacement(step.placement, isCenter),
          arrow: step.arrow ?? (isCenter ? false : { pointAtCenter: false }),
          closable: false,
          title: (
            <>
              <GuideTourHeader current={current} total={total} />
              <h3 className={styles.stepTitle}>{step.title}</h3>
            </>
          ),
          description: body,
        };
      }),
    [steps, current, total]
  );

  const resolvedGap = steps[current]?.gap ?? gap ?? { offset: 8, radius: 16 };
  const panelWidthCss = formatGuideTourPanelWidth(panelWidth);

  if (total === 0) {
    return null;
  }

  const tour = (
    <Tour
      open={open}
      current={current}
      onClose={handleSkip}
      onChange={setCurrent}
      onFinish={() => {
        onFinish?.();
        handleClose();
      }}
      steps={tourSteps}
      gap={resolvedGap}
      mask={mask}
      getPopupContainer={getPopupContainer}
      zIndex={10100}
      rootClassName={[styles.guideTour, rootClassName]
        .filter(Boolean)
        .join(" ")}
      className={className}
      style={
        {
          "--guide-tour-panel-width": panelWidthCss,
        } as CSSProperties
      }
      indicatorsRender={() => null}
      actionsRender={() => (
        <GuideTourActions
          current={current}
          total={total}
          labels={labels}
          onSkip={handleSkip}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}
      styles={{
        section: {
          width: panelWidthCss,
          maxWidth: "calc(100vw - 32px)",
        },
        header: { padding: 0, width: "100%" },
        description: { padding: 0 },
        footer: { padding: 0 },
      }}
    />
  );

  if (!locale) {
    return tour;
  }

  return <ConfigProvider locale={locale}>{tour}</ConfigProvider>;
}
